// =============================================================================
// LAYER REGISTRY — Configuración centralizada de capas del mapa
// =============================================================================
// Para añadir una capa nueva: añadir una entrada al array LAYER_REGISTRY
// + un GeoJSON en public/data/. Sin tocar LayoutPage.tsx.
// =============================================================================

import {
  TRACKER_STEPS, PILE_STEPS,
  VALLADO_STEPS, ZANJA_BT_STEPS, VIAL_STEPS,
  INVERSOR_STRING_STEPS, SUBESTACION_STEPS,
} from '@/types/plant'

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export interface StepConfig {
  code:       string
  label:      string
  shortLabel: string
  weight:     number
  color:      string
  glowColor:  string
}

export interface LayerConfig {
  id:               string          // 'tracker', 'zanja_bt', etc.
  label:            string          // nombre singular visible en UI
  labelPlural:      string          // 'Trackers', 'Zanjas BT', etc.
  sourceFile:       string          // fichero bajo public/data/ (o '' si es dinámico)
  mapSourceId:      string          // ID del source MapLibre
  mapLayerIds:      string[]        // IDs de los layers MapLibre (puede ser >1)
  geometryType:     'point' | 'line' | 'polygon'
  interactive:      boolean         // seleccionable + editable
  segmented:        boolean         // tramos de 500 m
  showInGlobalView: boolean         // visible sin parcela seleccionada
  defaultColor:     string          // color del elemento no iniciado
  steps:            readonly StepConfig[]
}

// ---------------------------------------------------------------------------
// Registro completo
// ---------------------------------------------------------------------------

export const LAYER_REGISTRY: readonly LayerConfig[] = [
  {
    id:               'tracker',
    label:            'Tracker',
    labelPlural:      'Trackers',
    sourceFile:       '',                          // dinámico — generado desde CSV
    mapSourceId:      'trackers',
    mapLayerIds:      ['trackers-glow', 'trackers-mid', 'trackers-line'],
    geometryType:     'line',
    interactive:      true,
    segmented:        false,
    showInGlobalView: true,
    defaultColor:     '#475569',
    steps:            TRACKER_STEPS,
  },
  {
    id:               'pile',
    label:            'Hinca',
    labelPlural:      'Hincas',
    sourceFile:       '',                          // dinámico — generado desde CSV
    mapSourceId:      'piles',
    mapLayerIds:      ['piles-circle', 'piles-damper'],
    geometryType:     'point',
    interactive:      true,
    segmented:        false,
    showInGlobalView: false,
    defaultColor:     '#475569',
    steps:            PILE_STEPS,
  },
  {
    id:               'parcel_perimeter',
    label:            'Perímetro parcela',
    labelPlural:      'Perímetros',
    sourceFile:       'parcel_perimeters.geojson',
    mapSourceId:      'parcel-perimeters',
    mapLayerIds:      ['parcel-perimeters-fill', 'parcel-perimeters-line'],
    geometryType:     'polygon',
    interactive:      false,
    segmented:        false,
    showInGlobalView: true,
    defaultColor:     '#F59E0B',
    steps:            [],
  },
  {
    id:               'vallado_tramo',
    label:            'Vallado',
    labelPlural:      'Vallado',
    sourceFile:       'vallado_tramos.geojson',
    mapSourceId:      'vallado-tramos',
    mapLayerIds:      ['vallado-tramos-fill', 'vallado-tramos-line'],
    geometryType:     'polygon',
    interactive:      true,
    segmented:        true,
    showInGlobalView: false,
    defaultColor:     '#A3E635',
    steps:            VALLADO_STEPS,
  },
  {
    id:               'zanja_bt',
    label:            'Zanja BT',
    labelPlural:      'Zanjas BT',
    sourceFile:       'zanja_bt_tramos.geojson',
    mapSourceId:      'zanja-bt',
    mapLayerIds:      ['zanja-bt-line'],
    geometryType:     'line',
    interactive:      true,
    segmented:        true,
    showInGlobalView: false,
    defaultColor:     '#F97316',
    steps:            ZANJA_BT_STEPS,
  },
  {
    id:               'tendido_bt',
    label:            'Tendido BT',
    labelPlural:      'Tendidos BT',
    sourceFile:       'tendido_bt.geojson',
    mapSourceId:      'tendido-bt',
    mapLayerIds:      ['tendido-bt-line'],
    geometryType:     'line',
    interactive:      false,
    segmented:        false,
    showInGlobalView: false,
    defaultColor:     '#FDE68A',
    steps:            [],
  },
  {
    id:               'vial',
    label:            'Vial',
    labelPlural:      'Viales',
    sourceFile:       'viales_tramos.geojson',
    mapSourceId:      'viales',
    mapLayerIds:      ['viales-line'],
    geometryType:     'line',
    interactive:      true,
    segmented:        true,
    showInGlobalView: false,
    defaultColor:     '#92400E',
    steps:            VIAL_STEPS,
  },
  {
    id:               'inversor_string',
    label:            'Inversor String',
    labelPlural:      'Inversores String',
    sourceFile:       'inversores_string.geojson',
    mapSourceId:      'inversores-string',
    mapLayerIds:      ['inversores-string-points'],
    geometryType:     'point',
    interactive:      true,
    segmented:        false,
    showInGlobalView: false,
    defaultColor:     '#06B6D4',
    steps:            INVERSOR_STRING_STEPS,
  },
  {
    id:               'subestacion',
    label:            'Subestación',
    labelPlural:      'Subestaciones',
    sourceFile:       'subestaciones.geojson',
    mapSourceId:      'subestaciones',
    mapLayerIds:      ['subestaciones-fill', 'subestaciones-line'],
    geometryType:     'polygon',
    interactive:      true,
    segmented:        false,
    showInGlobalView: true,
    defaultColor:     '#EF4444',
    steps:            SUBESTACION_STEPS,
  },
] as const

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Devuelve el LayerConfig para un id dado, o undefined. */
export function getLayer(id: string): LayerConfig | undefined {
  return (LAYER_REGISTRY as readonly LayerConfig[]).find(l => l.id === id)
}

/** Devuelve todos los layers interactivos con steps definidos. */
export function getInteractiveLayers(): LayerConfig[] {
  return (LAYER_REGISTRY as readonly LayerConfig[]).filter(
    l => l.interactive && l.steps.length > 0
  )
}

/** Devuelve todos los layers visibles en vista global. */
export function getGlobalViewLayers(): LayerConfig[] {
  return (LAYER_REGISTRY as readonly LayerConfig[]).filter(l => l.showInGlobalView)
}

/**
 * Dado el key de ELEMENT_TYPES usado en LayoutPage (e.g. 'trackers', 'hincas', 'zanjas_bt'),
 * devuelve los steps correctos para ese elemento.
 * Fallback: TRACKER_STEPS.
 */
export function getStepsForElementKey(elementKey: string): readonly StepConfig[] {
  const mapping: Record<string, string> = {
    trackers:     'tracker',
    hincas:       'pile',
    motoras:      'pile',
    vallado:      'vallado_tramo',
    zanjas_bt:    'zanja_bt',
    viales:       'vial',
    inversores:   'inversor_string',
    subestaciones:'subestacion',
  }
  const layerId = mapping[elementKey]
  if (!layerId) return TRACKER_STEPS
  return getLayer(layerId)?.steps ?? TRACKER_STEPS
}
